import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Lock,
  Bell,
  Globe,
  Shield,
  Database,
  Mail,
  Palette,
  Settings as SettingsIcon,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  Check,
  X,
  AlertTriangle,
  AlertCircle,
  Info,
  Upload,
  Trash2,
  Edit,
  Plus,
  Building,
  CreditCard,
  Smartphone,
  Monitor,
  Moon,
  Sun,
  Volume2,
  VolumeX,
  Wifi,
  HardDrive,
  Activity,
  Clock,
  MapPin,
  Phone,
  Calendar,
  FileText,
  Key,
  UserCheck,
  Users,
  Zap,
  Crown,
  DollarSign,
  ShieldCheck,
  CheckCircle,
  Loader2,
  Truck,
  Package,
  Tag,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { formatDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { updateAdminUser } from '../services/adminUserService';
import { 
  getShipFromAddresses, 
  createShipFromAddress, 
  updateShipFromAddress, 
  deleteShipFromAddress,
  validateShipFromAddress
} from '../services/shipFromAddressService';
import {
  getPackageDimensions,
  createPackageDimensions,
  updatePackageDimensions,
  deletePackageDimensions,
  validatePackageDimensions
} from '../services/packageDimensionsService';
import { HealthCheckService } from '../services/healthCheckService';
import { getMaintenanceConfig, setMaintenanceConfig, subscribeToMaintenanceConfig, MaintenanceConfig } from '../services/maintenanceService';
import { getRushdSettings, updateRushdSettings, validateRushdSettings, RushdSettings as RushdSettingsType } from '../services/rushdSettingsService';
import { ShipFromAddress, PackageDimensions, MiscItem } from '../types';
import { ShipFromAddressModal } from '../components/ShipFromAddressModal';
import { PackageDimensionsModal } from '../components/PackageDimensionsModal';
import { MiscItemModal } from '../components/MiscItemModal';
import { ShippingConfiguration, RateProvider } from '../components/ShippingConfigModal';
import { getMiscItems } from '../services/miscItemsService';
import { toast } from 'sonner';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

// Rush'd Settings Types and Service
interface RushdSettings {
  programActive: boolean;
  memberExpressPriceMode: 'percentage_off' | 'flat_price';
  memberRateValue: number;
  minOrderValueEnabled: boolean;
  minOrderValue: number;
  monthlyUsageLimitEnabled: boolean;
  monthlyUsageLimit: number;
  membersOnlyDiscountsEnabled: boolean;
  randomFreeTreatsEnabled: boolean;
  priorityPackingEnabled: boolean;
  effectiveFrom?: Date;
  lastModifiedBy?: string;
  lastModifiedAt?: Date;
}

const rushdApiService = {
  async getSettings(): Promise<RushdSettings> {
    const response = await fetch('/api/rushd/settings');
    const data = await response.json();
    if (!data.success) throw new Error(data.error?.message || 'Failed to fetch settings');
    return data.settings;
  },

  async updateSettings(settings: Partial<RushdSettings>): Promise<RushdSettings> {
    const response = await fetch('/api/rushd/settings', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-User-Id': 'admin-user-id',
      },
      body: JSON.stringify(settings),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.error?.message || 'Failed to update settings');
    return data.settings;
  }
};

// Dummy data for admin settings
const dummyAdminUsers = [
  {
    id: 'ADMIN-001',
    name: 'Marcus Campbell',
    email: 'marcus@jampackd.store',
    phone: '+1-876-555-0123',
    role: 'Super Admin',
    department: 'Administration',
    location: 'Kingston, Jamaica',
    bio: 'Experienced administrator with 10+ years in e-commerce management.',
  },
  {
    id: 'ADMIN-002',
    name: 'Sarah Johnson',
    email: 'sarah@jampackd.store',
    role: 'Manager',
    status: 'active',
    lastLogin: new Date('2024-01-24T16:45:00'),
    permissions: ['products', 'orders', 'customers'],
    avatar: null,
  },
  {
    id: 'ADMIN-003',
    name: 'David Thompson',
    email: 'david@jampackd.store',
    role: 'Support',
    status: 'inactive',
    lastLogin: new Date('2024-01-20T09:15:00'),
    permissions: ['orders', 'customers'],
    avatar: null,
  },
];

const dummySystemSettings = {
  siteName: 'JamPack\'d Admin Portal',
  siteUrl: 'https://admin.jampackd.store',
  timezone: 'America/New_York',
  currency: 'USD',
  language: 'en',
  maintenanceMode: false,
  debugMode: false,
  cacheEnabled: true,
  backupFrequency: 'daily',
  sessionTimeout: 30,
  maxLoginAttempts: 5,
  passwordExpiry: 90,
  twoFactorRequired: false,
};

const dummyNotificationSettings = {
  emailNotifications: true,
  smsNotifications: false,
  pushNotifications: true,
  orderAlerts: true,
  lowStockAlerts: true,
  securityAlerts: true,
  systemUpdates: true,
  marketingEmails: false,
  weeklyReports: true,
  monthlyReports: true,
  soundEnabled: true,
  desktopNotifications: true,
};

const dummyIntegrationSettings = {
  stripeConnected: true,
  paypalConnected: true,
  googleAnalytics: true,
  facebookPixel: false,
  mailchimpConnected: true,
  slackConnected: false,
  webhooksEnabled: true,
  apiRateLimit: 1000,
  corsEnabled: true,
  sslEnabled: true,
};

export function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser, userProfile, changePassword } = useAuth();
  
  // Profile settings state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Extract first and last name when profile loads
  useEffect(() => {
    if (userProfile?.name) {
      const nameParts = userProfile.name.split(' ');
      const first = nameParts[0] || '';
      // Join the rest of the array as the last name (handles middle names too)
      const last = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      
      setFirstName(first);
      setLastName(last);
      setOriginalName(userProfile.name);
    }
  }, [userProfile]);

  // Auto-refresh health checks when system tab is accessed
  useEffect(() => {
    if (activeTab === 'system') {
      refreshHealthChecks();
    }
  }, [activeTab]);

  // Load Rush'd settings on component mount (following shipping tab pattern)
  useEffect(() => {
    const loadRushdConfig = async () => {
      try {
        setLoadingRushd(true);
        const settings = await getRushdSettings();
        setRushdSettings(settings);
        setOriginalRushdSettings(settings);
        setHasRushdChanges(false);
      } catch (error) {
        console.error('Error loading Rush\'d settings:', error);
        toast.error('Failed to load Rush\'d settings');
      } finally {
        setLoadingRushd(false);
      }
    };

    loadRushdConfig();
  }, []);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // System health state
  const [maintenanceConfig, setMaintenanceConfigState] = useState<MaintenanceConfig>({
    enabled: false,
    message: 'We are currently performing scheduled maintenance. Please check back soon.',
    estimatedDuration: 'a few minutes',
    lastUpdated: new Date()
  });
  const [isUpdatingMaintenance, setIsUpdatingMaintenance] = useState(false);
  const [showMaintenanceConfirmation, setShowMaintenanceConfirmation] = useState(false);
  const [pendingMaintenanceState, setPendingMaintenanceState] = useState(false);
  const [healthChecks, setHealthChecks] = useState({
    customerApp: { status: 'healthy', responseTime: 245, lastChecked: new Date() },
    adminPortal: { status: 'healthy', responseTime: 189, lastChecked: new Date() },
    backend: { status: 'healthy', responseTime: 156, lastChecked: new Date() },
    database: { status: 'healthy', responseTime: 67, lastChecked: new Date() },
  });
  const [isRefreshingHealth, setIsRefreshingHealth] = useState(false);

  // System settings state
  const [systemData, setSystemData] = useState(dummySystemSettings);
  const [notificationData, setNotificationData] = useState(dummyNotificationSettings);
  const [integrationData, setIntegrationData] = useState(dummyIntegrationSettings);

  // Shipping configuration state
  const [shipFromAddresses, setShipFromAddresses] = useState<ShipFromAddress[]>([]);
  const [packageDimensions, setPackageDimensions] = useState<PackageDimensions[]>([]);
  const [miscItems, setMiscItems] = useState<MiscItem[]>([]);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [shippingConfig, setShippingConfig] = useState<ShippingConfiguration | null>(null);
  const [expressDiscountPercent, setExpressDiscountPercent] = useState<number>(0);
  const [rateProvider, setRateProvider] = useState<RateProvider>('easypost');
  const [isUpdatingShippingConfig, setIsUpdatingShippingConfig] = useState(false);
  
  // Modal states
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showDimensionsModal, setShowDimensionsModal] = useState(false);
  const [showMiscItemModal, setShowMiscItemModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState<ShipFromAddress | null>(null);
  const [editingDimensions, setEditingDimensions] = useState<PackageDimensions | null>(null);
  const [editingMiscItem, setEditingMiscItem] = useState<MiscItem | null>(null);

  // Rush'd settings state
  const [rushdSettings, setRushdSettings] = useState<RushdSettingsType>({
    programActive: false,
    memberExpressPriceMode: 'percentage_off',
    memberRateValue: 25,
    minOrderValueEnabled: false,
    minOrderValue: 50,
    monthlyUsageLimitEnabled: false,
    monthlyUsageLimit: 10,
  });
  const [loadingRushd, setLoadingRushd] = useState(false);
  const [savingRushd, setSavingRushd] = useState(false);
  const [hasRushdChanges, setHasRushdChanges] = useState(false);
  const [originalRushdSettings, setOriginalRushdSettings] = useState<RushdSettingsType | null>(null);
  const [previewOrder, setPreviewOrder] = useState({ expressRate: 35 });

  // Check if name has been changed
  const hasNameChanged = () => {
    const currentFullName = `${firstName} ${lastName}`.trim();
    return currentFullName !== originalName;
  };

  // Password validation function (same as customer app)
  const validatePassword = (password: string) => {
    const requirements = {
      minLength: password.length >= 6,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    
    return requirements;
  };

  const passwordRequirements = validatePassword(newPassword);
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  // Validate password confirmation
  useEffect(() => {
    if (confirmNewPassword && newPassword !== confirmNewPassword) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  }, [newPassword, confirmNewPassword]);

  // Load maintenance config on component mount
  useEffect(() => {
    const loadMaintenanceConfig = async () => {
      try {
        const config = await getMaintenanceConfig();
        setMaintenanceConfigState(config);
      } catch (error) {
        console.error('Error loading maintenance config:', error);
        toast.error('Failed to load maintenance configuration');
      }
    };

    loadMaintenanceConfig();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToMaintenanceConfig((config) => {
      setMaintenanceConfigState(config);
    });

    return () => unsubscribe();
  }, []);

  // Load main shipping configuration from Firebase
  const loadMainShippingConfig = async (): Promise<ShippingConfiguration | null> => {
    try {
      const configDoc = await getDoc(doc(db, 'shippingConfig', 'main'));
      
      if (configDoc.exists()) {
        const data = configDoc.data();
        return {
          id: configDoc.id,
          tiers: data.tiers || [],
          lastUpdated: data.lastUpdated?.toDate() || new Date(),
          version: data.version || '1.0.0',
          expressShippingDiscountPercent: data.expressShippingDiscountPercent || 0,
          rateProvider: (data.rateProvider as RateProvider) || 'easypost'
        };
      }
      return null;
    } catch (error) {
      console.error('Error loading main shipping config:', error);
      return null;
    }
  };

  // Save express shipping configuration (discount percentage and rate provider)
  const saveShippingDiscountConfig = async (discountPercent: number, provider?: RateProvider) => {
    if (!shippingConfig) return;

    try {
      setIsUpdatingShippingConfig(true);
      const updatedConfig = {
        ...shippingConfig,
        expressShippingDiscountPercent: discountPercent,
        rateProvider: provider || rateProvider,
        lastUpdated: new Date()
      };

      await setDoc(doc(db, 'shippingConfig', 'main'), updatedConfig);
      setShippingConfig(updatedConfig);
      setExpressDiscountPercent(discountPercent);
      if (provider) {
        setRateProvider(provider);
      }
      
      toast.success("Express shipping configuration updated successfully!");
    } catch (error) {
      console.error('Error saving shipping configuration:', error);
      toast.error("Failed to save shipping configuration");
    } finally {
      setIsUpdatingShippingConfig(false);
    }
  };

  // Save rate provider only
  const saveRateProvider = async (provider: RateProvider) => {
    await saveShippingDiscountConfig(expressDiscountPercent, provider);
  };

  // Load shipping configuration on component mount
  useEffect(() => {
    const loadShippingConfig = async () => {
      try {
        setLoadingShipping(true);
        const [addresses, dimensions, items, mainShippingConfig] = await Promise.all([
          getShipFromAddresses(),
          getPackageDimensions(),
          getMiscItems(),
          loadMainShippingConfig()
        ]);
        setShipFromAddresses(addresses);
        setPackageDimensions(dimensions);
        setMiscItems(items);
        if (mainShippingConfig) {
          setShippingConfig(mainShippingConfig);
          setExpressDiscountPercent(mainShippingConfig.expressShippingDiscountPercent);
          setRateProvider(mainShippingConfig.rateProvider || 'easypost');
        }
      } catch (error) {
        console.error('Error loading shipping configuration:', error);
        toast.error('Failed to load shipping configuration');
      } finally {
        setLoadingShipping(false);
      }
    };

    loadShippingConfig();
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordError("Please fill in all fields");
      return;
    }

    if (!isPasswordValid) {
      setPasswordError("Password does not meet all requirements");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError("New password must be different from current password");
      return;
    }

    setIsChangingPassword(true);
    setPasswordError("");

    try {
      await changePassword(currentPassword, newPassword);
      
      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      
      toast.success("Password changed successfully!");
    } catch (error: any) {
      console.error('Password change error:', error);
      
      // Handle specific Firebase error codes
      switch (error.code) {
        case 'auth/wrong-password':
          setPasswordError("Current password is incorrect");
          break;
        case 'auth/weak-password':
          setPasswordError("New password is too weak");
          break;
        case 'auth/requires-recent-login':
          setPasswordError("Please sign out and sign back in, then try again");
          break;
        default:
          setPasswordError("Failed to change password. Please try again.");
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Handle saving profile changes
  const handleSaveProfile = async () => {
    if (!currentUser || !userProfile) return;
    
    try {
      setIsUpdatingProfile(true);
      const fullName = `${firstName} ${lastName}`.trim();
      
      await updateAdminUser(currentUser.uid, { name: fullName });
      
      setOriginalName(fullName);
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleSaveSecurity = async () => {
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
  };

  const handleSaveSystem = async () => {
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
  };

  // Handle maintenance mode toggle
  const handleMaintenanceModeToggle = async (enabled: boolean) => {
    // Show confirmation dialog instead of directly toggling
    setPendingMaintenanceState(enabled);
    setShowMaintenanceConfirmation(true);
  };

  // Handle confirmed maintenance mode change
  const confirmMaintenanceModeChange = async () => {
    setIsUpdatingMaintenance(true);
    setShowMaintenanceConfirmation(false);
    
    try {
      await setMaintenanceConfig({ 
        enabled: pendingMaintenanceState,
        updatedBy: currentUser?.uid 
      });
      
      toast.success(
        pendingMaintenanceState 
          ? 'Maintenance mode enabled. Customer website is now offline.' 
          : 'Maintenance mode disabled. Customer website is now online.'
      );
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      toast.error('Failed to update maintenance mode. Please try again.');
    } finally {
      setIsUpdatingMaintenance(false);
    }
  };

  // Handle cancelled maintenance mode change
  const cancelMaintenanceModeChange = () => {
    setShowMaintenanceConfirmation(false);
    setPendingMaintenanceState(maintenanceConfig.enabled); // Reset to current state
  };

  // Handle health check refresh
  const refreshHealthChecks = async () => {
    setIsRefreshingHealth(true);
    try {
      const healthResults = await HealthCheckService.checkAllServices();
      setHealthChecks(healthResults);
      
      // Count healthy services for user feedback
      const healthyCount = Object.values(healthResults).filter(h => h.status === 'healthy').length;
      const totalCount = Object.values(healthResults).length;
      
      if (healthyCount === totalCount) {
        toast.success('All services are healthy!');
      } else if (healthyCount > 0) {
        toast.warning(`${healthyCount}/${totalCount} services are healthy. Check failed services.`);
      } else {
        toast.error('All services are experiencing issues. Please investigate.');
      }
    } catch (error) {
      console.error('Error refreshing health checks:', error);
      toast.error('Failed to refresh health checks. Please try again.');
    } finally {
      setIsRefreshingHealth(false);
    }
  };

  const getRoleConfig = (role: string) => {
    switch (role) {
      case 'super_admin':
        return { 
          color: 'bg-indigo-100 text-indigo-800', 
          icon: Crown,
          label: 'Super Admin',
          description: 'Full system access'
        };
      case 'manager':
        return { 
          color: 'bg-purple-100 text-purple-800', 
          icon: ShieldCheck,
          label: 'Manager',
          description: 'Business operations'
        };
      case 'support':
        return { 
          color: 'bg-blue-100 text-blue-800', 
          icon: UserCheck,
          label: 'Support',
          description: 'Customer service'
        };
      case 'viewer':
        return { 
          color: 'bg-gray-100 text-gray-800', 
          icon: Eye,
          label: 'Viewer',
          description: 'Read-only access'
        };
      default:
        return { 
          color: 'bg-gray-100 text-gray-800', 
          icon: Shield,
          label: 'Unknown',
          description: 'Unknown role'
        };
    }
  };

  const getOriginalRoleConfig = (role: string) => {
    switch (role) {
      case 'Super Admin':
        return { color: 'bg-purple-100 text-purple-800', icon: Shield };
      case 'Manager':
        return { color: 'bg-blue-100 text-blue-800', icon: Users };
      case 'Support':
        return { color: 'bg-green-100 text-green-800', icon: UserCheck };
      default:
        return { color: 'bg-gray-100 text-gray-800', icon: User };
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'bg-green-100 text-green-800', label: 'Active' };
      case 'inactive':
        return { color: 'bg-gray-100 text-gray-800', label: 'Inactive' };
      case 'suspended':
        return { color: 'bg-red-100 text-red-800', label: 'Suspended' };
      default:
        return { color: 'bg-gray-100 text-gray-800', label: 'Unknown' };
    }
  };

  // Shipping configuration handlers
  const handleDeleteAddress = async (addressId: string) => {
    try {
      await deleteShipFromAddress(addressId);
      const updatedAddresses = await getShipFromAddresses();
      setShipFromAddresses(updatedAddresses);
      toast.success('Address deleted successfully');
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error('Failed to delete address');
    }
  };

  const handleDeleteDimensions = async (dimensionsId: string) => {
    try {
      await deletePackageDimensions(dimensionsId);
      const updatedDimensions = await getPackageDimensions();
      setPackageDimensions(updatedDimensions);
      toast.success('Package dimensions deleted successfully');
    } catch (error) {
      console.error('Error deleting dimensions:', error);
      toast.error('Failed to delete package dimensions');
    }
  };

  const handleDeleteMiscItem = async (itemId: string) => {
    try {
      const { deleteMiscItem } = await import('../services/miscItemsService');
      await deleteMiscItem(itemId);
      const updatedItems = await getMiscItems();
      setMiscItems(updatedItems);
      toast.success('Misc item deleted successfully');
    } catch (error) {
      console.error('Error deleting misc item:', error);
      toast.error('Failed to delete misc item');
    }
  };

  // Modal handlers
  const handleAddressModalSuccess = async () => {
    const updatedAddresses = await getShipFromAddresses();
    setShipFromAddresses(updatedAddresses);
  };

  const handleDimensionsModalSuccess = async () => {
    const updatedDimensions = await getPackageDimensions();
    setPackageDimensions(updatedDimensions);
  };

  const handleMiscItemModalSuccess = async () => {
    const updatedItems = await getMiscItems();
    setMiscItems(updatedItems);
  };

  // Rush'd Settings Functions
  const refreshRushdSettings = async () => {
    try {
      setLoadingRushd(true);
      const settings = await getRushdSettings();
      setRushdSettings(settings);
      setOriginalRushdSettings(settings);
      setHasRushdChanges(false);
      toast.success('Rush\'d settings refreshed successfully');
    } catch (error) {
      console.error('Error refreshing Rush\'d settings:', error);
      toast.error('Failed to refresh Rush\'d settings');
    } finally {
      setLoadingRushd(false);
    }
  };

  const handleRushdSettingChange = (key: keyof RushdSettingsType, value: any) => {
    const newSettings = { ...rushdSettings, [key]: value };
    setRushdSettings(newSettings);
    setHasRushdChanges(JSON.stringify(newSettings) !== JSON.stringify(originalRushdSettings));
  };

  const handleSaveRushd = async () => {
    try {
      // Validate settings before saving
      const validationErrors = validateRushdSettings(rushdSettings);
      if (validationErrors.length > 0) {
        toast.error(`Validation failed: ${validationErrors.join(', ')}`);
        return;
      }

      setSavingRushd(true);
      const updatedSettings = await updateRushdSettings(rushdSettings, userProfile?.name || 'Unknown');
      setRushdSettings(updatedSettings);
      setOriginalRushdSettings(updatedSettings);
      setHasRushdChanges(false);
      toast.success('Rush\'d settings saved successfully!');
    } catch (error) {
      console.error('Error saving Rush\'d settings:', error);
      toast.error('Failed to save Rush\'d settings');
    } finally {
      setSavingRushd(false);
    }
  };


  const handleResetRushd = () => {
    if (originalRushdSettings) {
      setRushdSettings(originalRushdSettings);
      setHasRushdChanges(false);
    }
  };

  // Calculate preview pricing for Rush'd

  const renderProfileTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-jamaica-green" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First name</Label>
              <Input
                id="firstName" 
                placeholder="John" 
                value={firstName} 
                onChange={(e) => setFirstName(e.target.value)} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last name</Label>
              <Input
                id="lastName" 
                placeholder="Doe" 
                value={lastName} 
                onChange={(e) => setLastName(e.target.value)} 
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                  id="email" 
                  value={userProfile?.email || ''}
                  placeholder="admin@example.com" 
                  className="pl-10" 
                  disabled 
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Input
                id="role"
                value={getRoleConfig(userProfile?.role || '').label}
                placeholder="Role" 
                disabled
                className="bg-gray-50"
              />
            </div>
            </div>
        </CardContent>
        <div className="flex justify-end p-6 pt-0">
            <Button
              onClick={handleSaveProfile}
            disabled={!hasNameChanged() || isUpdatingProfile}
              className="bg-jamaica-green hover:bg-jamaica-green/90 text-white"
            >
            {isUpdatingProfile ? (
                <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
      </Card>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      {/* Password Change */}
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-jamaica-green" />
            Password
          </CardTitle>
          <p className="text-sm text-gray-600">Change your password.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
          <div className="space-y-2">
              <Label htmlFor="currentPassword">Current password</Label>
            <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <Input
                id="currentPassword"
                  type="password" 
                  placeholder="••••••••"
                  className="pl-10 py-3"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={isChangingPassword}
                />
            </div>
          </div>
          <div className="space-y-2">
              <Label htmlFor="newPassword">New password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <Input
              id="newPassword"
                  type="password" 
                  placeholder="••••••••"
                  className="pl-10 py-3"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isChangingPassword}
            />
          </div>
              {newPassword && (
                <div className="mt-2 p-3 bg-gray-50 rounded-md border">
                  <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
                  <div className="space-y-1">
                    <div className={`flex items-center text-xs ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                      {passwordRequirements.minLength ? (
                        <Check className="h-3 w-3 mr-2" />
                      ) : (
                        <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                      )}
                      At least 6 characters
          </div>
                    <div className={`flex items-center text-xs ${passwordRequirements.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                      {passwordRequirements.hasUppercase ? (
                        <Check className="h-3 w-3 mr-2" />
              ) : (
                        <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
              )}
                      One uppercase letter (A-Z)
          </div>
                    <div className={`flex items-center text-xs ${passwordRequirements.hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
                      {passwordRequirements.hasLowercase ? (
                        <Check className="h-3 w-3 mr-2" />
                      ) : (
                        <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                      )}
                      One lowercase letter (a-z)
            </div>
                    <div className={`flex items-center text-xs ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                      {passwordRequirements.hasNumber ? (
                        <Check className="h-3 w-3 mr-2" />
                      ) : (
                        <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                      )}
                      One number (0-9)
          </div>
                    <div className={`flex items-center text-xs ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`}>
                      {passwordRequirements.hasSpecialChar ? (
                        <Check className="h-3 w-3 mr-2" />
                      ) : (
                        <div className="h-3 w-3 mr-2 rounded-full border border-gray-300"></div>
                      )}
                      One special character (!@#$%^&*)
            </div>
                  </div>
                </div>
              )}
          </div>
          <div className="space-y-2">
              <Label htmlFor="confirmNewPassword">Confirm new password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <Input
                  id="confirmNewPassword" 
                  type="password" 
                  placeholder="••••••••"
                  className={`pl-10 py-3 ${
                    confirmNewPassword && newPassword !== confirmNewPassword
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                      : confirmNewPassword && newPassword === confirmNewPassword
                      ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                      : ""
                  }`}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  disabled={isChangingPassword}
                />
                {confirmNewPassword && (
                  <div className="absolute right-3 top-3">
                    {newPassword === confirmNewPassword ? (
                      <div className="h-5 w-5 text-green-500">
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
          </div>
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
    </div>
                )}
              </div>
            </div>

            {passwordError && (
              <div className="flex items-center text-red-600 text-sm bg-red-50 p-3 rounded-md">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                {passwordError}
          </div>
            )}

            <Button 
              type="submit"
              className="w-full bg-jamaica-green hover:bg-jamaica-green/90 text-white py-3"
              disabled={isChangingPassword || !currentPassword || !newPassword || !confirmNewPassword || !isPasswordValid || newPassword !== confirmNewPassword}
            >
              {isChangingPassword ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Changing Password...
                </>
              ) : (
                "Change Password"
              )}
                            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );

  // Removed renderTeamTab, renderNotificationsTab, and renderIntegrationsTab functions

  const renderSystemTab = () => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'healthy':
          return 'text-green-600 bg-green-50 border-green-200';
        case 'warning':
          return 'text-yellow-600 bg-yellow-50 border-yellow-200';
        case 'error':
          return 'text-red-600 bg-red-50 border-red-200';
        default:
          return 'text-gray-600 bg-gray-50 border-gray-200';
      }
    };

    const getStatusIcon = (status: string) => {
      switch (status) {
        case 'healthy':
          return <CheckCircle className="h-5 w-5 text-green-600" />;
        case 'warning':
          return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
        case 'error':
          return <X className="h-5 w-5 text-red-600" />;
        default:
          return <Activity className="h-5 w-5 text-gray-600" />;
      }
    };

    const formatResponseTime = (time: number) => {
      if (time < 100) return 'text-green-600';
      if (time < 300) return 'text-yellow-600';
      return 'text-red-600';
    };

    const apps = [
      {
        name: 'Customer Website',
        key: 'customerApp',
        description: 'JamPack\'d E-commerce Platform',
        icon: Globe,
        url: 'https://jampackd.store'
      },
      {
        name: 'Admin Portal',
        key: 'adminPortal',
        description: 'Administrative Dashboard',
        icon: Shield,
        url: 'https://admin.jampackd.store'
      },
      {
        name: 'Backend API',
        key: 'backend',
        description: 'Core API Services',
        icon: Database,
        url: 'API Services'
      },
      {
        name: 'Database',
        key: 'database',
        description: 'Firebase Firestore',
        icon: HardDrive,
        url: 'Cloud Database'
      }
    ];

    return (
      <div className="space-y-8">
        {/* Maintenance Mode Control */}
        <Card className="admin-card overflow-hidden">
          <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-green/5 p-6 border-b border-jamaica-green/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-jamaica-green/20 rounded-lg">
                  <SettingsIcon className="h-6 w-6 text-jamaica-green" />
              </div>
              <div>
                  <h3 className="text-lg font-semibold text-gray-900">Maintenance Mode</h3>
                  <p className="text-sm text-gray-600">Control customer website availability</p>
              </div>
            </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className={`text-sm font-medium ${maintenanceConfig.enabled ? 'text-red-600' : 'text-green-600'}`}>
                    {maintenanceConfig.enabled ? 'OFFLINE' : 'ONLINE'}
                  </p>
                  <p className="text-xs text-gray-500">Customer Site</p>
              </div>
              <Switch
                  checked={maintenanceConfig.enabled}
                  onCheckedChange={handleMaintenanceModeToggle}
                  disabled={isUpdatingMaintenance}
                  className="data-[state=checked]:bg-red-500"
              />
            </div>
          </div>
            {isUpdatingMaintenance && (
              <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating maintenance mode...
                </div>
            )}
              </div>
          {maintenanceConfig.enabled && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <p className="text-sm font-medium text-red-800">
                  Customer website is currently in maintenance mode and unavailable to visitors.
                </p>
                </div>
              </div>
          )}
        </Card>

        {/* System Health Dashboard */}
        <Card className="admin-card">
          <CardHeader className="border-b border-gray-100">
              <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">System Health</CardTitle>
                  <p className="text-sm text-gray-600 mt-1">Real-time status of all applications and services</p>
                </div>
              </div>
              <Button
                onClick={refreshHealthChecks}
                disabled={isRefreshingHealth}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshingHealth ? 'animate-spin' : ''}`} />
                {isRefreshingHealth ? 'Checking...' : 'Refresh'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {apps.map((app, index) => {
                const health = healthChecks[app.key as keyof typeof healthChecks];
                const statusColor = getStatusColor(health.status);
                
                return (
                  <div
                    key={app.key}
                    className={`p-6 border-b border-r-0 md:border-r border-gray-100 ${
                      index >= 2 ? 'border-b-0' : ''
                    } ${index % 2 === 1 ? 'md:border-r-0' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                          <app.icon className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                          <h4 className="font-medium text-gray-900">{app.name}</h4>
                          <p className="text-sm text-gray-500">{app.description}</p>
                </div>
              </div>
                      <div className={`px-3 py-1 rounded-full border text-xs font-medium ${statusColor}`}>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(health.status)}
                          {health.status.toUpperCase()}
            </div>
          </div>
    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
              <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Response Time</p>
                        <p className={`text-lg font-semibold ${formatResponseTime(health.responseTime)}`}>
                          {health.responseTime}ms
                        </p>
              </div>
              <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Last Checked</p>
                        <p className="text-sm text-gray-700">
                          {health.lastChecked.toLocaleTimeString()}
                        </p>
              </div>
            </div>
                    
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500">{app.url}</p>
              </div>
            </div>
                );
              })}
          </div>
        </CardContent>
      </Card>

        {/* System Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {Object.values(healthChecks).filter(h => h.status === 'healthy').length}
                  </p>
                  <p className="text-sm text-gray-600">Services Healthy</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="admin-card">
            <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {Math.round(Object.values(healthChecks).reduce((sum, h) => sum + h.responseTime, 0) / Object.values(healthChecks).length)}ms
                  </p>
                  <p className="text-sm text-gray-600">Avg Response Time</p>
                    </div>
                  </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Shield className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                  <p className="text-2xl font-bold text-gray-900">99.9%</p>
                  <p className="text-sm text-gray-600">System Uptime</p>
            </div>
          </div>
        </CardContent>
      </Card>
        </div>
    </div>
  );
  };

  const renderShippingTab = () => (
    <div className="space-y-6">
      {/* Shipping Configuration */}
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-jamaica-green" />
            Express Shipping Configuration
          </CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            Configure express shipping rate provider and discount percentage.
          </p>
        </CardHeader>
        <CardContent>
          {loadingShipping ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">Loading configuration...</span>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="express-discount">Express Shipping Discount (%)</Label>
                    <div className="flex">
                      <Input
                        id="express-discount"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={expressDiscountPercent}
                        onChange={(e) => setExpressDiscountPercent(parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="flex-1 rounded-r-none border-r-0"
                      />
                      <span className="flex items-center px-3 text-gray-500 bg-gray-50 border border-l-0 rounded-l-none rounded-r-md">%</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Discount applied to express shipping rates before displaying to customers. Range: 0-100%
                    </p>
                  </div>
                  <Button
                    onClick={() => saveShippingDiscountConfig(expressDiscountPercent)}
                    disabled={isUpdatingShippingConfig || !shippingConfig}
                    className="bg-jamaica-green hover:bg-jamaica-green/90"
                  >
                    {isUpdatingShippingConfig ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Discount
                      </>
                    )}
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label>Live Preview</Label>
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <p className="text-sm text-gray-600 mb-2">Express Shipping Rate Preview:</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Original Rate:</span>
                        <span className="font-mono">$35.00</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Discount ({expressDiscountPercent}%):</span>
                        <span className="font-mono text-red-600">-${(35 * (expressDiscountPercent / 100)).toFixed(2)}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between font-medium">
                        <span>Customer Sees:</span>
                        <span className="font-mono text-jamaica-green">
                          ${Math.floor(35 * (1 - expressDiscountPercent / 100)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Final amount is floored to nearest dollar as per currency conversion logic.
                    </p>
                  </div>
                </div>
              </div>

              {/* Rate Provider Selection */}
              <div className="border-t pt-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-base font-medium">Rate Provider</Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Choose the shipping rate calculation provider for express shipping
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* EasyPost Option */}
                    <div 
                      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all hover:border-gray-300 ${
                        rateProvider === 'easypost' 
                          ? 'border-jamaica-green bg-jamaica-green/5' 
                          : 'border-gray-200'
                      }`}
                      onClick={() => setRateProvider('easypost')}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="rateProvider"
                          value="easypost"
                          checked={rateProvider === 'easypost'}
                          onChange={() => setRateProvider('easypost')}
                          className="h-4 w-4 text-jamaica-green border-gray-300 focus:ring-jamaica-green"
                        />
                        <div className="flex items-center gap-3 flex-1">
                          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-blue-100 border border-blue-200">
                            <Package className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">EasyPost</div>
                            <div className="text-sm text-gray-600">Multi-carrier shipping API</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* DHL Direct Option */}
                    <div 
                      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all hover:border-gray-300 ${
                        rateProvider === 'dhl' 
                          ? 'border-jamaica-green bg-jamaica-green/5' 
                          : 'border-gray-200'
                      }`}
                      onClick={() => setRateProvider('dhl')}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="rateProvider"
                          value="dhl"
                          checked={rateProvider === 'dhl'}
                          onChange={() => setRateProvider('dhl')}
                          className="h-4 w-4 text-jamaica-green border-gray-300 focus:ring-jamaica-green"
                        />
                        <div className="flex items-center gap-3 flex-1">
                          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-red-100 border border-red-200">
                            <Zap className="h-5 w-5 text-red-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">DHL Direct</div>
                            <div className="text-sm text-gray-600">DHL Express MyDHL API</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Provider Status and Save Button */}
                  <div className="flex items-center justify-between pt-4">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Current Provider:</span>
                      <div className="flex items-center gap-1">
                        {rateProvider === 'dhl' ? (
                          <>
                            <Zap className="h-4 w-4 text-red-600" />
                            <span className="font-medium text-red-600">DHL Direct</span>
                          </>
                        ) : (
                          <>
                            <Package className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-blue-600">EasyPost</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => saveRateProvider(rateProvider)}
                      disabled={isUpdatingShippingConfig || !shippingConfig}
                      className="bg-jamaica-green hover:bg-jamaica-green/90"
                    >
                      {isUpdatingShippingConfig ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Provider
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                    <strong>Note:</strong> Changes take effect immediately. The system automatically falls back to the alternate provider if the primary fails.
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ship-From Addresses */}
      <Card className="admin-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-jamaica-green" />
                Ship-From Addresses
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Configure the addresses from which your packages will be shipped for EasyPost label generation.
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingAddress(null);
                setShowAddressModal(true);
              }}
              className="bg-jamaica-green hover:bg-jamaica-green/90 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Address
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingShipping ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">Loading addresses...</span>
            </div>
          ) : shipFromAddresses.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No addresses configured</h3>
              <p className="text-gray-600 mb-4">
                Add a ship-from address to enable automated label generation with EasyPost.
              </p>
              <Button
                onClick={() => {
                  setEditingAddress(null);
                  setShowAddressModal(true);
                }}
                className="bg-jamaica-green hover:bg-jamaica-green/90 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Address
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {shipFromAddresses.map((address) => (
                <div
                  key={address.id}
                  className={`p-4 rounded-lg border ${
                    address.isDefault
                      ? 'border-jamaica-green bg-jamaica-green/5'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium text-gray-900">{address.name}</h4>
                        {address.isDefault && (
                          <Badge className="bg-jamaica-green text-white">Default</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{address.company}</p>
                      <p className="text-sm text-gray-600">
                        {address.line1}
                        {address.line2 && `, ${address.line2}`}
                      </p>
                      <p className="text-sm text-gray-600">
                        {address.city}, {address.state} {address.zipCode}, {address.country}
                      </p>
                      <p className="text-sm text-gray-600">{address.phone}</p>
                      {address.email && (
                        <p className="text-sm text-gray-600">{address.email}</p>
                      )}
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Customs Signer:</span> {address.customsSigner}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingAddress(address);
                          setShowAddressModal(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAddress(address.id!)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Package Dimensions */}
      <Card className="admin-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-jamaica-green" />
                Package Dimensions
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Pre-configure common package dimensions for faster label generation.
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingDimensions(null);
                setShowDimensionsModal(true);
              }}
              className="bg-jamaica-green hover:bg-jamaica-green/90 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Dimensions
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingShipping ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">Loading dimensions...</span>
            </div>
          ) : packageDimensions.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No dimensions configured</h3>
              <p className="text-gray-600 mb-4">
                Add package dimensions to streamline the label generation process.
              </p>
              <Button
                onClick={() => {
                  setEditingDimensions(null);
                  setShowDimensionsModal(true);
                }}
                className="bg-jamaica-green hover:bg-jamaica-green/90 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Package Dimensions
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packageDimensions.map((dimensions) => (
                <div
                  key={dimensions.id}
                  className={`p-4 rounded-lg border ${
                    dimensions.isDefault
                      ? 'border-jamaica-green bg-jamaica-green/5'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium text-gray-900">{dimensions.name}</h4>
                        {dimensions.isDefault && (
                          <Badge className="bg-jamaica-green text-white text-xs">Default</Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <strong>Dimensions:</strong> {dimensions.length}" × {dimensions.width}" × {dimensions.height}"
                        </p>
                        {dimensions.weight && (
                          <p>
                            <strong>Weight:</strong> {dimensions.weight} oz
                          </p>
                        )}
                        {dimensions.description && (
                          <p className="text-xs text-gray-500">{dimensions.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingDimensions(dimensions);
                        setShowDimensionsModal(true);
                      }}
                      className="flex-1"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteDimensions(dimensions.id!)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Misc Items */}
      <Card className="admin-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-jamaica-green" />
                Miscellaneous Items
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Configure items like thank you cards and documentation that can be included in packages.
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingMiscItem(null);
                setShowMiscItemModal(true);
              }}
              className="bg-jamaica-green hover:bg-jamaica-green/90 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingShipping ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">Loading misc items...</span>
            </div>
          ) : miscItems.length === 0 ? (
            <div className="text-center py-8">
              <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No misc items configured</h3>
              <p className="text-gray-600 mb-4">
                Add items like thank you cards that can be included in packages for customs declarations.
              </p>
              <Button
                onClick={() => {
                  setEditingMiscItem(null);
                  setShowMiscItemModal(true);
                }}
                className="bg-jamaica-green hover:bg-jamaica-green/90 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Misc Item
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {miscItems.map((item) => (
                <div
                  key={item.id}
                  className={`p-4 rounded-lg border ${
                    item.isActive
                      ? 'border-gray-200 bg-white'
                      : 'border-gray-100 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className={`font-medium ${item.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                          {item.name}
                        </h4>
                        {item.category && (
                          <Badge variant="outline" className="text-xs">
                            {item.category}
                          </Badge>
                        )}
                        {!item.isActive && (
                          <Badge variant="secondary" className="text-xs">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <p className={`text-sm mb-2 ${item.isActive ? 'text-gray-600' : 'text-gray-400'}`}>
                        {item.description}
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Weight:</span>
                          <span className="ml-1 font-medium">{item.weight} {item.weightUnit}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Value:</span>
                          <span className="ml-1 font-medium">${item.value.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Origin:</span>
                          <span className="ml-1 font-medium">{item.originCountry}</span>
                        </div>
                        {item.hsCode && (
                          <div>
                            <span className="text-gray-500">HS Code:</span>
                            <span className="ml-1 font-medium">{item.hsCode}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingMiscItem(item);
                          setShowMiscItemModal(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteMiscItem(item.id!)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderRushdTab = () => {
    if (loadingRushd) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-jamaica-green" />
            <p className="text-gray-600">Loading Rush'd settings...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Action buttons */}
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
          <Button
            onClick={handleSaveRushd}
            disabled={!hasRushdChanges || savingRushd}
            className="bg-jamaica-green hover:bg-jamaica-green/90"
          >
            {savingRushd ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
          
          <Button
            variant="outline"
            onClick={handleResetRushd}
            disabled={!hasRushdChanges}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          
          <Button
            variant="outline"
            onClick={refreshRushdSettings}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          
          {hasRushdChanges && (
            <Badge variant="outline" className="border-amber-300 text-amber-700">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Unsaved Changes
            </Badge>
          )}
        </div>

        <div className="space-y-6">
            {/* Program Status */}
            <Card className="admin-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-jamaica-green" />
                  Program Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Rush'd Program Active</Label>
                    <p className="text-sm text-gray-600">
                      Enable or disable the entire Rush'd membership program
                    </p>
                  </div>
                  <Switch
                    checked={rushdSettings.programActive}
                    onCheckedChange={(checked) => handleRushdSettingChange('programActive', checked)}
                  />
                </div>
                
                {!rushdSettings.programActive && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Program Disabled: All Rush'd features are currently inactive
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Pricing Configuration */}
            <Card className="admin-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-jamaica-green" />
                  Member Pricing Configuration
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Configure how member pricing is calculated for Rush'd Express shipping rates.
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Pricing Mode</Label>
                      <p className="text-sm text-gray-600 mb-3">
                        How member pricing is calculated for Express shipping
                      </p>
                      <Select 
                        value={rushdSettings.memberExpressPriceMode} 
                        onValueChange={(value: 'percentage_off' | 'flat_price') => 
                          handleRushdSettingChange('memberExpressPriceMode', value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage_off">Percentage Off Carrier Rate</SelectItem>
                          <SelectItem value="flat_price">Flat Member Price</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-base font-medium">
                        {rushdSettings.memberExpressPriceMode === 'percentage_off' ? 'Discount Percentage' : 'Flat Rate Price'}
                      </Label>
                      <p className="text-sm text-gray-600 mb-3">
                        {rushdSettings.memberExpressPriceMode === 'percentage_off' 
                          ? 'Percentage discount applied to DHL Express rates'
                          : 'Fixed price charged to members for Express shipping'
                        }
                      </p>
                      <div className="flex items-center gap-2">
                        {rushdSettings.memberExpressPriceMode === 'flat_price' && <span className="text-gray-500">$</span>}
                        <Input
                          type="number"
                          value={rushdSettings.memberRateValue}
                          onChange={(e) => handleRushdSettingChange('memberRateValue', Number(e.target.value))}
                          min={0}
                          max={rushdSettings.memberExpressPriceMode === 'percentage_off' ? 100 : undefined}
                          className="w-32"
                        />
                        {rushdSettings.memberExpressPriceMode === 'percentage_off' && <span className="text-gray-500">%</span>}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Live Preview</Label>
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <p className="text-sm text-gray-600 mb-2">Rush'd Member Rate Preview:</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>Original Rate:</span>
                          <span className="font-mono">${previewOrder.expressRate.toFixed(2)}</span>
                        </div>
                        {rushdSettings.memberExpressPriceMode === 'percentage_off' ? (
                          <>
                            <div className="flex justify-between">
                              <span>Discount ({rushdSettings.memberRateValue}%):</span>
                              <span className="font-mono text-red-600">-${(previewOrder.expressRate * (rushdSettings.memberRateValue / 100)).toFixed(2)}</span>
                            </div>
                            <Separator />
                            <div className="flex justify-between font-medium">
                              <span>Member Price:</span>
                              <span className="font-mono text-jamaica-green">
                                ${(previewOrder.expressRate * (1 - rushdSettings.memberRateValue / 100)).toFixed(2)}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex justify-between">
                              <span>Savings:</span>
                              <span className="font-mono text-red-600">-${(previewOrder.expressRate - rushdSettings.memberRateValue).toFixed(2)}</span>
                            </div>
                            <Separator />
                            <div className="flex justify-between font-medium">
                              <span>Member Price:</span>
                              <span className="font-mono text-jamaica-green">
                                ${rushdSettings.memberRateValue.toFixed(2)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        <Input
                          type="number"
                          value={previewOrder.expressRate}
                          onChange={(e) => setPreviewOrder(prev => ({ ...prev, expressRate: Number(e.target.value) }))}
                          className="text-xs h-6 w-20 inline mr-1"
                        />
                        <span>sample express rate</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Eligibility Requirements */}
            <Card className="admin-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-jamaica-green" />
                  Eligibility Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-base font-medium">Minimum Order Value</Label>
                      <p className="text-sm text-gray-600">
                        Require a minimum cart value to use member pricing
                      </p>
                    </div>
                    <Switch
                      checked={rushdSettings.minOrderValueEnabled}
                      onCheckedChange={(checked) => handleRushdSettingChange('minOrderValueEnabled', checked)}
                    />
                  </div>
                  
                  {rushdSettings.minOrderValueEnabled && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">$</span>
                      <Input
                        type="number"
                        value={rushdSettings.minOrderValue}
                        onChange={(e) => handleRushdSettingChange('minOrderValue', Number(e.target.value))}
                        min={0}
                        className="w-32"
                      />
                    </div>
                  )}
                </div>
                
                <Separator />
                
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-base font-medium">Monthly Usage Limit</Label>
                      <p className="text-sm text-gray-600">
                        Limit how many times member pricing can be used per month
                      </p>
                    </div>
                    <Switch
                      checked={rushdSettings.monthlyUsageLimitEnabled}
                      onCheckedChange={(checked) => handleRushdSettingChange('monthlyUsageLimitEnabled', checked)}
                    />
                  </div>
                  
                  {rushdSettings.monthlyUsageLimitEnabled && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={rushdSettings.monthlyUsageLimit}
                        onChange={(e) => handleRushdSettingChange('monthlyUsageLimit', Number(e.target.value))}
                        min={1}
                        className="w-32"
                      />
                      <span className="text-gray-500">orders per month</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
        
        {/* Last Modified Info */}
        {rushdSettings.lastModifiedAt && (
          <Card className="admin-card">
            <CardContent className="py-4">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>
                    Last modified: {new Date(rushdSettings.lastModifiedAt).toLocaleString()}
                  </span>
                </div>
                {rushdSettings.lastModifiedBy && (
                  <span>by {rushdSettings.lastModifiedBy}</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes fadeInSlide {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      <div className="space-y-6">
        {/* Settings Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white rounded-xl shadow-md border border-gray-100"
        >
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {[
                { id: 'profile', label: 'Profile', icon: User, disabled: false },
                { id: 'security', label: 'Security', icon: Lock, disabled: false },
                { id: 'system', label: 'System', icon: SettingsIcon, disabled: false },
                { id: 'shipping', label: 'Shipping', icon: Truck, disabled: false },
                { id: 'rushd', label: 'Rush\'d', icon: Crown, disabled: false },
                { id: 'notifications', label: 'Notifications', icon: Bell, disabled: true },
              ].map(({ id, label, icon: Icon, disabled }) => (
                <button
                  key={id}
                  onClick={() => !disabled && setActiveTab(id)}
                  disabled={disabled}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === id && !disabled
                      ? 'border-jamaica-green text-jamaica-green'
                      : disabled
                      ? 'border-transparent text-gray-400 cursor-not-allowed opacity-60'
                      : 'border-transparent text-gray-500 hover:text-gray-700 cursor-pointer'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'security' && renderSecurityTab()}
            {activeTab === 'system' && renderSystemTab()}
            {activeTab === 'shipping' && renderShippingTab()}
            {activeTab === 'rushd' && renderRushdTab()}
            {activeTab === 'notifications' && (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Notifications Coming Soon
                </h3>
                <p className="text-gray-600">
                  This feature is currently in development and will be available soon.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Maintenance Mode Confirmation Dialog */}
      <Dialog open={showMaintenanceConfirmation} onOpenChange={setShowMaintenanceConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              {pendingMaintenanceState ? 'Enable Maintenance Mode' : 'Disable Maintenance Mode'}
            </DialogTitle>
            <DialogDescription className="text-left">
              {pendingMaintenanceState ? (
                <>
                  <p className="mb-3">
                    <strong>Warning:</strong> Enabling maintenance mode will make the customer-facing website 
                    <span className="text-red-600 font-medium"> temporarily unavailable</span> to all visitors.
                  </p>
                  <p className="mb-3">This will:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 mb-3">
                    <li>Display a maintenance page to customers</li>
                    <li>Prevent new orders and user registrations</li>
                    <li>Block access to all customer features</li>
                    <li>Potentially impact sales and user experience</li>
                  </ul>
                  <p className="text-sm text-gray-700">
                    Only enable maintenance mode if you need to perform critical updates or maintenance tasks.
                  </p>
                </>
              ) : (
                <>
                  <p className="mb-3">
                    Are you sure you want to <span className="text-green-600 font-medium">disable maintenance mode</span> 
                    and make the customer website available again?
                  </p>
                  <p className="text-sm text-gray-600">
                    This will restore full access to the customer-facing website and all its features.
                  </p>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={cancelMaintenanceModeChange}
              disabled={isUpdatingMaintenance}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmMaintenanceModeChange}
              disabled={isUpdatingMaintenance}
              className={`${
                pendingMaintenanceState 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isUpdatingMaintenance ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {pendingMaintenanceState ? 'Enabling...' : 'Disabling...'}
                </>
              ) : (
                <>
                  {pendingMaintenanceState ? 'Enable Maintenance Mode' : 'Disable Maintenance Mode'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ship-From Address Modal */}
      <ShipFromAddressModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSuccess={handleAddressModalSuccess}
        address={editingAddress}
        mode={editingAddress ? 'edit' : 'add'}
      />

      {/* Package Dimensions Modal */}
      <PackageDimensionsModal
        isOpen={showDimensionsModal}
        onClose={() => setShowDimensionsModal(false)}
        onSuccess={handleDimensionsModalSuccess}
        dimensions={editingDimensions}
        mode={editingDimensions ? 'edit' : 'add'}
      />

      {/* Misc Item Modal */}
      <MiscItemModal
        isOpen={showMiscItemModal}
        onClose={() => setShowMiscItemModal(false)}
        onSuccess={handleMiscItemModalSuccess}
        item={editingMiscItem}
        mode={editingMiscItem ? 'edit' : 'add'}
      />
    </>
  );
} 